import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'

import { Box } from '@chakra-ui/react'
import { useCurrentMember } from 'src/features/auth/context'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'
import { useVideoCallRoom } from 'src/features/videoCall/context/videoCallRoomContext'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { useThisSession } from './useThisSession'
import { FloatingVideoPlayer } from '../components/FloatingVideoPlayer/FloatingVideoPlayer'
import { useRoomMediaContext } from '../context/MediaProvider'

export const useFloatingVideoCallForMember = ({
  sessionUid,
}: {
  sessionUid: string
}) => {
  const { currentMember } = useCurrentMember()
  const { isForcedExitByOtherDevice } = useVideoCallRoom()
  const { initializeMemberVideoCall } = useRoomMediaContext()

  // 初回の強制退出状態を保持するためのref
  // 初めからtrueだった場合はtoastを表示しない
  const firstForcedExitByOtherDeviceState = useRef(isForcedExitByOtherDevice)

  const thisSession = useThisSession({ sessionUid, forPatient: false })

  const isInPerson = useMemo(() => {
    if (!thisSession.fetched) return false
    return thisSession.explanationType === 'InPerson'
  }, [thisSession])

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  useEffect(() => {
    if (!thisSession.fetched) return
    if (isInPerson) return
    if (!thisSession.videoCallRoomUid) return

    initializeMemberVideoCall({
      roomUid: thisSession.videoCallRoomUid,
      member: currentMember,
    })
  }, [thisSession, isInPerson, initializeMemberVideoCall, currentMember])

  const sendNotification = useCallback(() => {
    if (!thisSession.fetched) return
    sendVideoCallStartedNotification({
      trialPatientUid: thisSession.patient.trialPatientUid,
      scene: 'Explanation',
      sendTo:
        thisSession.explanationType === 'Remote' ? 'Patient' : 'PartnerMembers',
    })
  }, [thisSession, sendVideoCallStartedNotification])

  const subscribe = useCallback((onStoreChange: () => void) => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(() => onStoreChange())
    })
    observer.observe(document.body)
    return () => observer.disconnect()
  }, [])

  const bodyHeight = useSyncExternalStore(subscribe, () => {
    return document.body.clientHeight
  })
  const bodyWidth = useSyncExternalStore(subscribe, () => {
    return document.body.clientWidth
  })

  const toast = useMirohaToast()

  useEffect(() => {
    if (
      !firstForcedExitByOtherDeviceState.current &&
      isForcedExitByOtherDevice
    ) {
      toast({
        status: 'info',
        title: '他の端末でビデオ通話が開始されたため退出しました。',
      })
    }
  }, [isForcedExitByOtherDevice, toast])

  const renderFloatingVideoCall = useCallback(
    (position?: { right: number; bottom: number }) => {
      if (isInPerson || isForcedExitByOtherDevice) {
        return null
      }
      const right = position?.right ?? 24
      const bottom = position?.bottom ?? 24
      const maxHeightPx = bodyHeight - bottom * 2
      const maxWidthPx = bodyWidth - right * 2
      return (
        <Box
          position="absolute"
          right={`${right}px`}
          bottom={`${bottom}px`}
          zIndex="docked" // PDFビューアーの要素より上に表示する
        >
          <FloatingVideoPlayer
            maxHeightPx={maxHeightPx}
            maxWidthPx={maxWidthPx}
            canShareScreen={true}
            sendNotification={sendNotification}
          />
        </Box>
      )
    },
    [
      isInPerson,
      isForcedExitByOtherDevice,
      bodyHeight,
      bodyWidth,
      sendNotification,
    ],
  )

  return {
    renderFloatingVideoCall,
  }
}
